import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Bug, Lightbulb, MessageCircle } from 'lucide-react';
import { useFeedback } from '../../context/FeedbackContext';
import { submitFeedback, type FeedbackCategory, type FeedbackData } from '../../services/feedback/feedbackService';
import { getRouteFromPath } from '../../utils/router';
import { getApiUrl } from '../../services/feedback/feedbackService';

// Cloudflare Turnstile widget type (based on official API)
declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement | string, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: (errorCode: string) => void;
        'expired-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact' | 'flexible';
        execution?: 'render' | 'execute';
        appearance?: 'always' | 'execute' | 'interaction-only';
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
      isExpired: (widgetId?: string) => boolean;
      execute: (element: HTMLElement | string, options?: { reset?: boolean }) => void;
      ready: (callback: () => void) => void;
    };
  }
}

export function FeedbackForm() {
  const { isOpen, closeFeedback } = useFeedback();
  const [category, setCategory] = useState<FeedbackCategory>('feedback');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const turnstileWidgetRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  // Get current route for context
  const currentRoute = getRouteFromPath(window.location.pathname);
  const apiUrl = getApiUrl();

  // Load Turnstile site key
  useEffect(() => {
    const loadSiteKey = async () => {
      try {
        // Try to get from API first
        const response = await fetch(`${apiUrl}/api/turnstile/site-key`);
        const data = await response.json();
        if (data.siteKey) {
          setTurnstileSiteKey(data.siteKey);
          return;
        }
      } catch (error) {
        console.warn('Failed to load Turnstile site key from API:', error);
      }
      
      // Fallback to environment variable
      const envSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
      if (envSiteKey) {
        setTurnstileSiteKey(envSiteKey);
      }
    };
    
    loadSiteKey();
  }, [apiUrl]);

  // Set up Turnstile widget
  useEffect(() => {
    if (!isOpen || !turnstileSiteKey || !turnstileWidgetRef.current) return;
    
    let widgetId: string | null = null;

    // Use turnstile.ready() for proper initialization (recommended by Cloudflare)
    if (window.turnstile) {
      window.turnstile.ready(() => {
        const widgetContainer = turnstileWidgetRef.current;
        if (!widgetContainer) return;

        // Render Turnstile widget with explicit rendering
        widgetId = window.turnstile!.render(widgetContainer, {
          sitekey: turnstileSiteKey,
          callback: (token: string) => {
            setTurnstileToken(token);
          },
          'error-callback': (errorCode: string) => {
            console.error('Turnstile error:', errorCode);
            setTurnstileToken(null);
          },
          'expired-callback': () => {
            console.warn('Turnstile token expired');
            setTurnstileToken(null);
          },
          theme: 'auto',
          size: 'normal',
        });

        turnstileWidgetIdRef.current = widgetId;
      });
    } else {
      // Fallback: Wait for Turnstile script to load (shouldn't happen with explicit rendering)
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          window.turnstile.ready(() => {
            const widgetContainer = turnstileWidgetRef.current;
            if (!widgetContainer) return;

            widgetId = window.turnstile!.render(widgetContainer, {
              sitekey: turnstileSiteKey,
              callback: (token: string) => {
                setTurnstileToken(token);
              },
              'error-callback': (errorCode: string) => {
                console.error('Turnstile error:', errorCode);
                setTurnstileToken(null);
              },
              'expired-callback': () => {
                console.warn('Turnstile token expired');
                setTurnstileToken(null);
              },
              theme: 'auto',
              size: 'normal',
            });

            turnstileWidgetIdRef.current = widgetId;
          });
        }
      }, 100);

      // Cleanup interval after 10 seconds (script should load much faster)
      setTimeout(() => clearInterval(checkTurnstile), 10000);
    }

    return () => {
      // Cleanup: Remove widget when component unmounts or form closes
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [isOpen, turnstileSiteKey]);

  // Reset form when closed
  useEffect(() => {
    if (!isOpen) {
      // Small delay to allow close animation
      setTimeout(() => {
        setCategory('feedback');
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
        setSubmitSuccess(false);
        setTurnstileToken(null);
        // Reset Turnstile widget
        if (turnstileWidgetIdRef.current && window.turnstile) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
      }, 300);
    }
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closeFeedback();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeFeedback]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !message.trim()) {
      return;
    }

    if (!turnstileToken) {
      alert('Please complete the Turnstile verification.');
      return;
    }

    setIsSubmitting(true);

    const feedbackData: FeedbackData = {
      category,
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      subject: subject.trim(),
      message: message.trim(),
      currentRoute,
      userAgent: navigator.userAgent,
      turnstileToken: turnstileToken,
    };



    try {
      await submitFeedback(feedbackData);
      setSubmitSuccess(true);
      setTimeout(() => {
        closeFeedback();
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit feedback. Please try again.';
      alert(errorMessage);
      // Reset Turnstile on error
      setTurnstileToken(null);
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories: Array<{ value: FeedbackCategory; label: string; icon: React.ReactNode }> = [
    { value: 'feedback', label: 'Feedback', icon: <MessageCircle size={18} /> },
    { value: 'bug', label: 'Bug Report', icon: <Bug size={18} /> },
    { value: 'feature', label: 'Feature Request', icon: <Lightbulb size={18} /> },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeFeedback}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
          />

          {/* Slide-in Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl z-[9999] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Send Feedback
              </h2>
              <button
                onClick={closeFeedback}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close feedback form"
              >
                <X size={24} className="text-gray-600 dark:text-gray-300" />
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {submitSuccess ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                    <Send size={32} className="text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Thank You!
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Your feedback has been submitted. We'll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Category
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {categories.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => setCategory(cat.value)}
                          className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                            category === cat.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {cat.icon}
                          <span className="text-xs font-medium">{cat.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name (Optional) */}
                  <div>
                    <label htmlFor="feedback-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Name <span className="text-gray-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="feedback-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>

                  {/* Email (Optional) */}
                  <div>
                    <label htmlFor="feedback-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email <span className="text-gray-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="feedback-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="feedback-subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="feedback-subject"
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Brief description of your feedback"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="feedback-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Please provide details about your feedback, bug report, or feature request..."
                    />
                  </div>

                  {/* Cloudflare Turnstile Widget */}
                  <div className="flex flex-col items-center my-4">
                    {turnstileSiteKey ? (
                      <>
                        <div ref={turnstileWidgetRef} className="cf-turnstile" />
                        {turnstileToken && (
                          <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                            ✓ Turnstile verified
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        Turnstile not configured. Please set TURNSTILE_SITE_KEY.
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !subject.trim() || !message.trim()}
                    className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Send Feedback
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

